import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { filter, map, startWith } from 'rxjs';
import { Router, NavigationEnd } from '@angular/router';
import { IconComponent } from '../icon/icon.component';
import { HttpErrorHandlerService } from '../../../core/http-error/http-error-handler.service';
import { UserContextService } from '../../services/user-context/user-context.service';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'dpqa-http-error-status-bar',
  imports: [CommonModule, NzAlertModule, IconComponent],
  templateUrl: './http-error-status-bar.component.html',
  styleUrl: './http-error-status-bar.component.less',
})
export class HttpErrorStatusBarComponent {
  private readonly httpErrorHandler = inject(HttpErrorHandlerService);
  private readonly userContextService = inject(UserContextService);
  private readonly router = inject(Router);

  private readonly errorEvent = toSignal(this.httpErrorHandler.error$, { initialValue: null });

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => this.router.url),
      startWith(this.router.url),
    ), { initialValue: this.router.url },
  );

  // strip query/fragment, normalize trailing slashes, and enforce a path-boundary when checking prefixes to avoid `/slides` matching `/slides2`.
  public message = computed(() => {
    const ev = this.errorEvent();
    if (!ev || !ev.message) { return null; }
    const origin = ev.route || '';
    const cur = this.currentUrl() || '';
    if (!origin) { return ev.message; }

    const normalize = (u: string) => {
      const p = (u || '').split(/[?#]/)[0];
      return (p.length > 1 && p.endsWith('/')) ? p.slice(0, -1) : p;
    };

    const o = normalize(origin);
    const c = normalize(cur);

    const matches = c === o || c.startsWith(`${o}/`) || o.startsWith(`${c}/`);
    return matches ? ev.message : null;
  });

  public tenantDisplayName = computed(() => this.userContextService.tenantConfig()?.displayName || '');
  public version = DPQA_CLOUD_VERSION;
}
